/**
 * Pipeline Configuration
 * ======================
 * Single source of truth for status display, grouping, and labels.
 */

// Canonical statuses (matches backend /api/pipeline/statuses)
export const PIPELINE_STATUSES = [
  "New",
  "Quote Requested",
  "Awaiting Quote",
  "Quote Received",
  "Quote Submitted",
  "Quote Awaiting Approval",
  "Approved",
  "PO Issued",
  "Assigned",
  "Scheduled",
  "In Progress",
  "Work Completed",
  "Ready to Invoice",
  "Approved for Invoicing",
  "Invoice Submitted",
  "Invoiced",
  "Paid",
  "Closed",
];

// Pipeline columns for board view
export const PIPELINE_COLUMNS = [
  {
    key: "NEW",
    label: "New",
    color: "slate",
    bgColor: "bg-slate-100",
    borderColor: "border-slate-300",
    statuses: ["New", "Job Created", "Created"],
  },
  {
    key: "QUOTE",
    label: "Quote Pipeline",
    color: "amber",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-300",
    statuses: ["Quote Requested", "Awaiting Quote", "Quote Received", "Quote Submitted", "Quote Awaiting Approval"],
  },
  {
    key: "APPROVED",
    label: "Approved",
    color: "emerald",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-300",
    statuses: ["Approved", "PO Issued"],
  },
  {
    key: "SCHEDULING",
    label: "Awaiting Scheduling",
    color: "indigo",
    bgColor: "bg-indigo-50",
    borderColor: "border-indigo-300",
    statuses: ["Awaiting Scheduling", "Awaiting Assignment", "Unassigned", "Reassign Required"],
  },
  {
    key: "LIVE",
    label: "Approved Work",
    color: "blue",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-300",
    statuses: ["Assigned", "Scheduled", "In Progress"],
  },
  {
    key: "COMPLETED",
    label: "Work Completed",
    color: "green",
    bgColor: "bg-green-50",
    borderColor: "border-green-300",
    statuses: ["Work Completed", "Completed"],
  },
  {
    key: "FINANCIAL",
    label: "Invoicing",
    color: "purple",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-300",
    statuses: ["Ready to Invoice", "Approved for Invoicing", "Invoice Submitted", "Invoiced"],
  },
  {
    key: "DONE",
    label: "Closed",
    color: "slate",
    bgColor: "bg-slate-50",
    borderColor: "border-slate-200",
    statuses: ["Paid", "Closed"],
  },
];

// Human-readable status labels
export const STATUS_LABELS = {
  "New": "New Job",
  "Job Created": "New Job",
  "Quote Requested": "Awaiting Contractor Quote",
  "Awaiting Quote": "Awaiting Contractor Quote",
  "Quote Received": "Quote Received – Review Required",
  "Quote Submitted": "Quote Received – Review Required",
  "Quote Awaiting Approval": "Quote Awaiting Client Approval",
  "Approved": "Approved – Ready to Assign",
  "PO Issued": "PO Issued – Ready to Start",
  "Assigned": "Assigned to Contractor",
  "Awaiting Assignment": "Awaiting Assignment",
  "Scheduled": "Scheduled – Work Pending",
  "In Progress": "Work In Progress",
  "Work Completed": "Work Done – Awaiting Review",
  "Completed": "Work Done – Awaiting Review",
  "Ready to Invoice": "Approved for Billing",
  "Approved for Invoicing": "Approved for Billing",
  "Invoice Submitted": "Invoice Under Review",
  "Invoiced": "Invoice Validated",
  "Paid": "Payment Received",
  "Closed": "Job Closed",
};

// Status badge colors
export const STATUS_COLORS = {
  "New": "bg-slate-100 text-slate-700 border-slate-300",
  "Job Created": "bg-slate-100 text-slate-700 border-slate-300",
  "Quote Requested": "bg-amber-100 text-amber-700 border-amber-300",
  "Awaiting Quote": "bg-amber-100 text-amber-700 border-amber-300",
  "Quote Received": "bg-amber-100 text-amber-700 border-amber-300",
  "Quote Submitted": "bg-amber-100 text-amber-700 border-amber-300",
  "Quote Awaiting Approval": "bg-orange-100 text-orange-700 border-orange-300",
  "Approved": "bg-emerald-100 text-emerald-700 border-emerald-300",
  "PO Issued": "bg-emerald-100 text-emerald-700 border-emerald-300",
  "Assigned": "bg-blue-100 text-blue-700 border-blue-300",
  "Awaiting Assignment": "bg-blue-100 text-blue-700 border-blue-300",
  "Scheduled": "bg-cyan-100 text-cyan-700 border-cyan-300",
  "In Progress": "bg-cyan-100 text-cyan-700 border-cyan-300",
  "Work Completed": "bg-green-100 text-green-700 border-green-300",
  "Completed": "bg-green-100 text-green-700 border-green-300",
  "Ready to Invoice": "bg-purple-100 text-purple-700 border-purple-300",
  "Approved for Invoicing": "bg-purple-100 text-purple-700 border-purple-300",
  "Invoice Submitted": "bg-purple-100 text-purple-700 border-purple-300",
  "Invoiced": "bg-violet-100 text-violet-700 border-violet-300",
  "Paid": "bg-emerald-100 text-emerald-700 border-emerald-300",
  "Closed": "bg-slate-100 text-slate-600 border-slate-300",
};

// Get column for a job status
export function getColumnForStatus(status) {
  const column = PIPELINE_COLUMNS.find(col => col.statuses.includes(status));
  return column || PIPELINE_COLUMNS[0];
}

// Get jobs for a specific column
export function getJobsForColumn(jobs, column) {
  return jobs.filter(j => column.statuses.includes(j.status));
}

// Get human-readable label for status
export function getStatusLabel(status) {
  return STATUS_LABELS[status] || status;
}

// Get badge color classes for status
export function getStatusColor(status) {
  return STATUS_COLORS[status] || "bg-gray-100 text-gray-700 border-gray-300";
}

// Group jobs by pipeline column
export function groupJobsByColumn(jobs) {
  const grouped = {};
  
  PIPELINE_COLUMNS.forEach(column => {
    grouped[column.key] = {
      ...column,
      jobs: getJobsForColumn(jobs, column),
      count: 0,
    };
    grouped[column.key].count = grouped[column.key].jobs.length;
  });
  
  return grouped;
}

export default {
  PIPELINE_STATUSES,
  PIPELINE_COLUMNS,
  STATUS_LABELS,
  STATUS_COLORS,
  getColumnForStatus,
  getJobsForColumn,
  getStatusLabel,
  getStatusColor,
  groupJobsByColumn,
};
